import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "../api";
import { AuthContext } from "./AuthContext";
import { useToast } from "../components/ToastProvider";

// Global, app-wide scan state. Polls GET /domain-monitors/scan/status so EVERY
// page (via the header) knows when an on-demand or scheduled scan is running —
// even one started on another tab or by the cron worker — and shows live
// progress. The DomainMonitors page and the header both read from here, so
// there's a single source of truth and a single poll loop.
export const ScanContext = createContext(null);

export function ScanProvider({ children }) {
  const { isLoggedIn } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const toast = useToast();

  const [running, setRunning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  // Lets startScan/cancelScan force an immediate poll instead of waiting for the
  // next scheduled tick. Set inside the polling effect below.
  const kickRef = useRef(() => {});
  // Tracks the previous running state so we can fire a completion toast exactly
  // once, on the running → idle transition.
  const wasRunningRef = useRef(false);

  const poll = useCallback(async () => {
    let s;
    try {
      s = await api.get("/domain-monitors/scan/status");
    } catch {
      return null; // transient hiccup — keep whatever we had
    }
    setRunning(Boolean(s.running));
    setCancelling(Boolean(s.cancelling));
    setProgress(s.progress || null);
    setLastResult(s.lastResult || null);

    // Scan just finished — announce the outcome globally and refresh the list.
    if (wasRunningRef.current && !s.running) {
      queryClient.invalidateQueries({ queryKey: ["monitors"] });
      const r = s.lastResult || {};
      if (r.error) {
        toast.error(r.error);
      } else if (r.cancelled) {
        toast.info(
          `Scan cancelled — ${r.checked ?? 0} of ${r.total ?? 0} checked`
        );
      } else {
        toast.success(
          `Scan complete — ${r.up ?? 0} up, ${r.down ?? 0} down${
            r.total ? ` of ${r.total}` : ""
          }`
        );
      }
      setCancelling(false);
    }
    wasRunningRef.current = Boolean(s.running);
    return s;
  }, [queryClient, toast]);

  // Polling loop. A scan can ONLY start from the "Scan All" button (no cron /
  // run-on-start), so there's no need to poll constantly when idle. We poll
  // once on mount (to catch a scan still running after a refresh) and then keep
  // polling ONLY while a scan is actually running (every 2.5s). When idle we
  // stop entirely — no background traffic. startScan()/visibility re-kick it.
  useEffect(() => {
    if (!isLoggedIn) {
      setRunning(false);
      setProgress(null);
      wasRunningRef.current = false;
      return;
    }
    let active = true;
    let timer;
    const run = async () => {
      if (!active) return;
      if (typeof document !== "undefined" && document.hidden) return;
      const s = await poll();
      if (!active) return;
      // Keep polling only while a scan is in progress; otherwise go quiet.
      if (s && s.running) {
        clearTimeout(timer);
        timer = setTimeout(run, 2500);
      }
    };
    kickRef.current = () => {
      if (active) {
        clearTimeout(timer);
        run();
      }
    };
    run(); // one check on mount
    const onVis = () => {
      if (!document.hidden) kickRef.current();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      active = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
      kickRef.current = () => {};
    };
  }, [isLoggedIn, poll]);

  const startScan = useCallback(async () => {
    await api.post("/domain-monitors/scan"); // 202 — started or attached
    setRunning(true);
    wasRunningRef.current = true; // so the completion toast fires on finish
    kickRef.current(); // poll now → switch to fast cadence immediately
  }, []);

  const cancelScan = useCallback(async () => {
    setCancelling(true);
    try {
      await api.post("/domain-monitors/scan/cancel");
    } finally {
      kickRef.current();
    }
  }, []);

  return (
    <ScanContext.Provider
      value={{
        running,
        cancelling,
        progress,
        lastResult,
        startScan,
        cancelScan,
      }}
    >
      {children}
    </ScanContext.Provider>
  );
}

export function useScan() {
  const ctx = useContext(ScanContext);
  if (!ctx) {
    throw new Error("useScan must be used within a ScanProvider");
  }
  return ctx;
}

export default ScanProvider;
